import {
  cloneElement,
  useRef,
  type CSSProperties,
  type ReactElement,
  type TouchEvent,
} from 'react';
import { useLocation } from 'react-router-dom';
import { sensX } from '../../i18n';
import { useOnglets } from './tabs';
import { useTabNav } from './TabNav';

/** Course minimale avant de decider que le geste est horizontal. */
const ENGAGE_PX = 12;

/**
 * Les onglets a monter pour couvrir les positions donnees.
 *
 * Une position entre deux onglets en demande deux. On y ajoute celle du
 * lacher : pendant le rattrapage, l'ecran qu'on abandonne doit rester a
 * l'image le temps de sortir, sinon il disparait d'un coup au lieu de
 * glisser.
 */
function framesAround(marks: number[]): number[] {
  const tabs = new Set<number>();
  for (const mark of marks) {
    tabs.add(Math.floor(mark));
    tabs.add(Math.ceil(mark));
  }
  return [...tabs].sort((a, b) => a - b);
}

/**
 * Le passage d'un onglet a l'autre, suivi au doigt.
 *
 * Les ecrans forment une bande horizontale dont on ne montre jamais plus
 * de deux morceaux : ceux qui encadrent la position en cours. On voit
 * partir celui qu'on quitte et arriver celui qu'on appelle, a la vitesse
 * du doigt. Sous un tiers de l'ecran, lacher ramene la ou l'on etait — un
 * geste qu'on ne peut pas annuler se termine toujours par une erreur.
 *
 * Chaque morceau est identifie par son rang, pas par sa place a l'image.
 * C'est ce qui evite le double chargement : l'ecran monte une fois quand
 * il pointe le bout du nez, et c'est le meme qui reste quand on arrive.
 */
/** Un emplacement complet, tel que `Routes` l'attend. */
function toLocation(path: string) {
  const [pathname, search] = path.split('?');
  return { pathname, search: search ? `?${search}` : '', hash: '', state: null, key: 'peek' };
}

/** L'adresse d'ou vient un ecran pose par-dessus un autre, s'il y en a. */
function fondDe(emplacement: { state?: unknown }): { pathname: string } | null {
  const etat = emplacement.state as { fond?: { pathname: string } } | null;
  return etat?.fond ?? null;
}

export function TabSwipe({ children }: { children: ReactElement }) {
  const { index, drag, backPeek, begin, moveTo, end } = useTabNav();
  const { chemins } = useOnglets();
  const location = useLocation();

  const start = useRef<{ x: number; y: number } | null>(null);
  const engaged = useRef(false);
  /** La zone defilante figee pendant le geste, et sa position. */
  const frozen = useRef<{ el: HTMLElement; top: number } | null>(null);

  /*
    Pendant un geste horizontal, le contenu ne defile plus.

    `touch-action: pan-y` laisse le navigateur gerer le vertical, ce qui
    est necessaire pour detecter le geste sans bloquer le defilement — mais
    il continue de le gerer une fois le geste reconnu, et l'ecran partait
    alors en diagonale. On fige donc la zone le temps du balayage. Le
    passage en `hidden` conserve la position, on la repose malgre tout par
    precaution.
  */
  function freezeScroll(container: HTMLElement) {
    // La bande courante, pas celle qui arrive : elle n'est pas encore posee.
    const el = container
      .querySelector<HTMLElement>(`[data-tab='${index}']`)
      ?.querySelector<HTMLElement>('.scroll-area');
    if (!el) return;

    frozen.current = { el, top: el.scrollTop };
    el.style.overflowY = 'hidden';
    el.scrollTop = frozen.current.top;
  }

  function thawScroll() {
    const held = frozen.current;
    frozen.current = null;
    if (!held) return;

    held.el.style.overflowY = '';
    held.el.scrollTop = held.top;
  }

  function onTouchStart(event: TouchEvent<HTMLDivElement>) {
    if (index === -1 || drag) return;
    /*
      Une fenetre ouverte garde ses gestes.

      La bande de visages de la feuille de partage defile a l'horizontale,
      exactement comme les onglets : partir la-dedans changeait d'onglet
      au lieu de faire glisser les amis, et le post qu'on voulait partager
      disparaissait avec le fil. Depuis un fil de commentaires il n'y
      avait rien a voler, ce qui expliquait que le meme geste marche a un
      endroit et pas a l'autre.

      Le marqueur est celui que la fenetre pose deja sur la racine pour
      figer le defilement du fond.
    */
    if (document.documentElement.classList.contains('sheet-open')) return;
    /* Une carte se promene au doigt : glisser y deplace la carte, pas l'onglet. */
    if ((event.target as Element).closest('[data-sans-balayage]')) return;

    const touch = event.touches[0];
    if (!touch) return;

    start.current = { x: touch.clientX, y: touch.clientY };
    engaged.current = false;
  }

  function onTouchMove(event: TouchEvent<HTMLDivElement>) {
    const from = start.current;
    const touch = event.touches[0];
    if (!from || !touch) return;

    /* Mesure dans le sens de lecture : en arabe, l'onglet suivant est a gauche. */
    const dx = (touch.clientX - from.x) * sensX;
    const dy = touch.clientY - from.y;

    if (!engaged.current) {
      // Plutot vertical : c'est un defilement, on rend la main pour de bon.
      if (Math.abs(dy) > Math.abs(dx)) {
        start.current = null;
        return;
      }
      if (Math.abs(dx) < ENGAGE_PX) return;

      // Un balayage n'appelle que le voisin : on ne saute pas d'ecran.
      if (!begin(dx < 0 ? [index, index + 1] : [index - 1, index])) {
        start.current = null;
        return;
      }

      engaged.current = true;
      freezeScroll(event.currentTarget);
    }

    // Une largeur d'ecran parcourue vaut un onglet, en sens inverse.
    moveTo(index - dx / (event.currentTarget.clientWidth || 1));
  }

  function onTouchEnd() {
    start.current = null;
    if (!engaged.current) return;

    engaged.current = false;
    thawScroll();
    end();
  }

  /*
    Les bandes sont identifiees par le chemin qu'elles rendent, jamais par
    leur place a l'image. C'est ce qui evite le double chargement : l'ecran
    monte une fois quand il pointe le bout du nez, et c'est le meme qui
    reste quand on arrive — le peek d'un geste de retour devient l'ecran
    courant sans se remonter.

    Le chemin seul, sans les parametres. Un onglet qui porte son etat dans
    l'adresse — la recherche du fil, `?q=...` — en changerait a chaque
    lettre tapee : la bande se remonterait, le champ serait detruit et
    recree, et le clavier retomberait entre deux frappes. Deux onglets ne
    se distinguent jamais par leurs parametres, de toute facon.
  */
  /*
    Un ecran ouvert par-dessus un autre ne change pas la bande.

    La page d'une sortie ouverte depuis la carte se pose au-dessus, et
    l'adresse change — mais l'ecran du dessous, lui, ne bouge pas. Sans
    cela sa cle changeait, et React le demontait puis le remontait : la
    carte se reconstruisait pour un aller-retour. Le lien porte l'adresse
    d'ou il part, et c'est elle qui compte ici.
  */
  const here = (fondDe(location) ?? location).pathname;

  /*
    Le chemin qu'on quitte, fige pour toute la duree du geste de retour.

    Mesure sur l'appareil : un seul retour montait l'ecran d'arrivee
    **trois fois**, et la vague d'apparition se rejouait a chaque fois —
    c'est le « double chargement ». La cause tient a deux instants
    distincts : la navigation part avant la fin du glissement, si bien
    qu'entre les deux `here` vaut deja le chemin d'arrivee. La bande qui
    glisse portait alors la meme cle que celle de dessous, et deux enfants
    de meme cle se resolvent par un demontage-remontage.

    En gelant le chemin de depart, les deux bandes gardent des cles
    distinctes du debut a la fin, et celle d'arrivee traverse le geste
    sans bouger.
  */
  const partant = useRef(here);
  if (!backPeek) partant.current = here;

  const pos = drag ? drag.pos : index;
  const shown = framesAround(drag ? [drag.from, pos] : [pos]);

  /*
    Les bandes sont toujours rendues sous la meme forme : un tableau.

    C'est ce qui manquait, et c'est ce qui provoquait le rechargement d'un
    geste de retour abandonne en chemin. Le geste posait l'ecran precedent
    derriere, ce qui faisait basculer d'un rendu a l'autre ; l'un donnait
    ses bandes en enfants separes, l'autre en tableau. React ne reconcilie
    pas ces deux formes de la meme facon — les cles d'un tableau vivent
    dans leur propre liste — si bien que la bande courante etait demontee
    puis remontee, et relisait ses donnees. On voyait l'ecran clignoter et
    se recharger pour un geste qui n'avait rien decide.

    Une seule liste, meme longueur de chaine : la bande gardant la cle
    `here` traverse le geste sans bouger.
  */
  /*
    Sous un ecran pose par-dessus, la bande ne bouge pas.

    Le geste de retour fait glisser cet ecran-la sur ce qui reste affiche
    dessous — la carte, intacte. Monter en plus la bande de derriere
    reviendrait a afficher deux fois le meme ecran, sous la meme cle, et
    React s'y perdrait.
  */
  const pique = fondDe(location) ? null : backPeek;

  const bandes = pique
    ? [
        /*
          Geste de retour : l'ecran precedent est pose derriere, et celui
          qu'on quitte glisse dessus. Il avance moins vite — il vient de
          plus loin, c'est ce decalage qui fait la profondeur.
        */
        /*
          Elle se rend comme la bande d'arrivee se rendra.

          Des que la navigation a rattrape le peek — `here` vaut alors le
          chemin de dessous — cette bande rend `children` tel quel, comme
          le fera la bande unique quand le geste sera fini. Sans cela, le
          passage de l'un a l'autre changeait la forme de l'enfant au
          moment meme ou les deux bandes n'en faisaient plus qu'une, et
          l'ecran se remontait.
        */
        <div key={pique} className="tab-swipe__pane tab-swipe__pane--under">
          {pique === here ? children : cloneElement(children, { location: toLocation(pique) })}
        </div>,
        /*
          Sa cle est figee, son contenu ne l'est pas.

          La cle, pour que les deux bandes n'en portent jamais la meme —
          c'est cette collision qui remontait l'ecran d'arrivee au milieu
          du geste. Le contenu, lui, reste `children` tel quel : lui poser
          un emplacement fige au depart du geste changeait la forme de
          l'enfant, l'ecran qu'on quitte se remontait, et le doigt perdait
          son fil. Il fallait alors balayer deux fois pour sortir.
        */
        <div key={partant.current} className="tab-swipe__pane">
          {children}
        </div>,
      ]
    : shown.map((tab) => (
        <div
          key={tab === index ? here : chemins[tab]}
          data-tab={tab}
          className="tab-swipe__pane"
          style={{ '--i': tab } as CSSProperties}
        >
          {tab === index
            ? children
            : cloneElement(children, { location: toLocation(chemins[tab]) })}
        </div>
      ));

  return (
    <div
      className={
        pique
          ? 'tab-swipe tab-swipe--back'
          : `tab-swipe${drag ? ' tab-swipe--dragging' : ''}${
              drag?.settling ? ' tab-swipe--settling' : ''
            }`
      }
      style={pique ? undefined : ({ '--pos': pos } as CSSProperties)}
      /* Pendant un retour, le balayage d'onglets n'a rien a decider. */
      onTouchStart={backPeek ? undefined : onTouchStart}
      onTouchMove={backPeek ? undefined : onTouchMove}
      onTouchEnd={backPeek ? undefined : onTouchEnd}
      onTouchCancel={backPeek ? undefined : onTouchEnd}
    >
      {bandes}
    </div>
  );
}
